import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Menu, Search, Filter, ChevronDown, ArrowUp, ArrowDown, RefreshCw, Download,
  Check, X, Edit2, Trash2,
} from 'lucide-react';
import DropdownPortal from '../components/DropdownPortal';
import { api } from '../lib/api';

export default function Transactions({ incomeData, loadIncome, allSources, toggleSidebar }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportButtonRect, setExportButtonRect] = useState(null);
  const exportBtnRef = useRef(null);

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const processedData = useMemo(() => {
    let data = [...incomeData];
    data = data.filter(item => {
      const searchMatch =
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.amount.toString().includes(searchTerm);
      const sourceMatch = sourceFilter === 'All' || item.source === sourceFilter;
      const dateMatch =
        (!dateRange.start || item.date >= dateRange.start) &&
        (!dateRange.end || item.date <= dateRange.end);
      const highValueMatch = activeQuickFilter === 'high' ? item.amount >= 5000 : true;
      return searchMatch && sourceMatch && dateMatch && highValueMatch;
    });

    if (sortConfig.key) {
      data.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (sortConfig.key === 'amount') { valA = parseFloat(valA); valB = parseFloat(valB); }
        if (sortConfig.key === 'date') { valA = new Date(valA); valB = new Date(valB); }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [incomeData, searchTerm, sourceFilter, dateRange, activeQuickFilter, sortConfig]);

  const totalFiltered = processedData.reduce((acc, curr) => acc + curr.amount, 0);

  const applyQuickFilter = (type) => {
    if (activeQuickFilter === type) { setActiveQuickFilter(null); setDateRange({ start: '', end: '' }); return; }
    setActiveQuickFilter(type);
    const today = new Date();
    if (type === '7days') {
      const past = new Date(today); past.setDate(today.getDate() - 7);
      setDateRange({ start: past.toISOString().split('T')[0], end: today.toISOString().split('T')[0] });
    } else if (type === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({ start: firstDay.toISOString().split('T')[0], end: today.toISOString().split('T')[0] });
    } else if (type === 'high') { setDateRange({ start: '', end: '' }); }
  };

  const clearAllFilters = () => {
    setSearchTerm(''); setSourceFilter('All'); setDateRange({ start: '', end: '' });
    setActiveQuickFilter(null); setSortConfig({ key: 'date', direction: 'desc' });
  };

  const handleSort = (key) => {
    setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc' });
  };

  const SortIndicator = ({ column }) => {
    if (sortConfig.key !== column) return <span className="sort-icon-placeholder" />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const toggleSelectionMode = () => { setIsSelectionMode(!isSelectionMode); setSelectedIds([]); };
  const handleSelectAll = (e) => { if (e.target.checked) setSelectedIds(processedData.map(i => i.id)); else setSelectedIds([]); };

  const handleRowClick = (id, e) => {
    if (e.target.closest('button') || e.target.closest('.action-cell')) return;
    if (isSelectionMode) {
      if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
      else setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSingleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/income/delete/${id}`); loadIncome(); }
    catch (err) { alert("Delete failed"); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} items?`)) return;
    try { await api.post(`/income/delete-bulk`, { ids: selectedIds }); loadIncome(); setSelectedIds([]); setIsSelectionMode(false); }
    catch (err) { alert("Bulk delete failed"); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try { await api.put(`/income/update/${editingItem.id}`, editingItem); loadIncome(); setEditingItem(null); }
    catch (err) { alert("Update failed"); }
  };

  const toggleExport = () => {
    if (showExportMenu) setShowExportMenu(false);
    else {
      if (exportBtnRef.current) setExportButtonRect(exportBtnRef.current.getBoundingClientRect());
      setShowExportMenu(true);
    }
  };

  const handleExcelExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      const wb = XLSX.utils.book_new();

      const avgEntry = totalFiltered > 0 ? Math.round(totalFiltered / processedData.length) : 0;

      const sheetData = [
        ["GIGFLOW FINANCIAL REPORT"],
        ["Generated on:", new Date().toLocaleDateString()],
        [],
        ["SUMMARY METRICS"],
        ["Total Revenue", `Rs. ${totalFiltered.toLocaleString('en-IN')}`],
        ["Total Transactions", processedData.length],
        ["Avg. Per Entry", `Rs. ${avgEntry.toLocaleString('en-IN')}`],
        [],
        ["TRANSACTION DETAILS"],
        ["DATE", "SOURCE", "DESCRIPTION", "AMOUNT (INR)"],
        ...processedData.map(item => [
          item.date,
          item.source,
          item.description || "-",
          item.amount
        ]),
        [],
        ["", "", "NET REVENUE:", totalFiltered]
      ];

      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 15 }];

      XLSX.utils.book_append_sheet(wb, ws, "Income Statement");
      XLSX.writeFile(wb, `GigFlow_Statement_${new Date().toISOString().split('T')[0]}.xlsx`);

      setIsExporting(false);
      setShowExportMenu(false);
    }, 500);
  };

  const handlePDFExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      const doc = new jsPDF();

      const deepNavy = "#0F172A";
      const textLight = "#64748B";
      const textDark = "#1E293B";

      doc.setFont("times", "bold");
      doc.setFontSize(28);
      doc.setTextColor(deepNavy);
      doc.text("GigFlow.", 14, 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(textLight);
      doc.text("FINANCIAL ANALYTICS & FORECASTING", 14, 28);

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(deepNavy);
      doc.text("INCOME STATEMENT", 196, 22, { align: "right" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(textLight);
      doc.text(`Date: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, 196, 28, { align: "right" });

      doc.setDrawColor(200, 200, 200);
      doc.line(14, 35, 196, 35);

      doc.setFillColor(248, 250, 252);
      doc.rect(14, 45, 182, 25, 'F');

      const avgEntry = totalFiltered > 0 ? Math.round(totalFiltered / processedData.length) : 0;

      const drawMetric = (label, value, x) => {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(textLight);
        doc.text(label, x, 53);

        doc.setFontSize(14);
        doc.setTextColor(deepNavy);
        doc.text(value, x, 62);
      };

      drawMetric("TOTAL REVENUE", `Rs. ${totalFiltered.toLocaleString('en-IN')}`, 20);
      drawMetric("TRANSACTIONS", `${processedData.length} Entries`, 85);
      drawMetric("AVG. PER ENTRY", `Rs. ${avgEntry.toLocaleString('en-IN')}`, 150);

      autoTable(doc, {
        startY: 80,
        head: [['DATE', 'SOURCE', 'DESCRIPTION', 'AMOUNT']],
        body: processedData.map(item => [
          item.date,
          item.source,
          item.description || "-",
          `Rs. ${item.amount.toLocaleString('en-IN')}`
        ]),
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 6,
          textColor: textDark,
        },
        headStyles: {
          fillColor: deepNavy,
          textColor: 255,
          fontStyle: "bold",
          textTransform: "uppercase"
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251],
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 40, fontStyle: 'bold' },
          3: { halign: 'right', fontStyle: 'bold' },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`GigFlow Analytics • Confidential Financial Statement`, 14, 290);
          doc.text(`Page ${pageCount}`, 196, 290, { align: "right" });
        }
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont("times", "bold");
      doc.setTextColor(deepNavy);
      doc.text("NET REVENUE", 140, finalY);
      doc.text(`Rs. ${totalFiltered.toLocaleString('en-IN')}`, 196, finalY, { align: "right" });

      doc.setDrawColor(deepNavy);
      doc.setLineWidth(0.5);
      doc.line(14, finalY + 5, 196, finalY + 5);

      doc.save(`GigFlow_Report_${Date.now()}.pdf`);
      setIsExporting(false);
      setShowExportMenu(false);
    }, 500);
  };

  return (
    <motion.div
      className="transactions-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="page-header-enhanced" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="mobile-menu-btn" onClick={toggleSidebar}><Menu size={24} /></button>
          <div><h1 className="page-title">Transactions</h1><p className="page-subtitle">History & Exports</p></div>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filters-top-row">
          <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="clear-search"><X size={14} /></button>}
          </div>
          <div className="filter-dropdown">
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}><option value="All">All Sources</option>{allSources.map(s => <option key={s} value={s}>{s}</option>)}</select>
            <ChevronDown size={14} className="dropdown-arrow" />
          </div>
          <div className="date-range-wrapper">
            <div className="date-input"><input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} /></div>
            <span className="date-separator">to</span>
            <div className="date-input"><input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} /></div>
          </div>
          <button className="btn-clear-filters" onClick={clearAllFilters} title="Reset"><RefreshCw size={16} /></button>

          <button className={`btn-select-toggle ${isSelectionMode ? 'active' : ''}`} onClick={toggleSelectionMode}>
            {isSelectionMode ? <X size={16} /> : <Check size={16} />} Select
          </button>

          <div className="export-wrapper" style={{ position: 'relative' }}>
            <button
              ref={exportBtnRef}
              className={`btn-export ${showExportMenu ? 'active' : ''}`}
              onClick={toggleExport}
            >
              {isExporting ? <div className="spinner-small"></div> : <Download size={16} />} Export
            </button>
          </div>
        </div>
        <div className="filters-chips-row">
          <span className="chips-label"><Filter size={14} /> Quick Filters:</span>
          <button className={`filter-chip ${activeQuickFilter === '7days' ? 'active' : ''}`} onClick={() => applyQuickFilter('7days')}>Last 7 Days</button>
          <button className={`filter-chip ${activeQuickFilter === 'month' ? 'active' : ''}`} onClick={() => applyQuickFilter('month')}>This Month</button>
          <button className={`filter-chip ${activeQuickFilter === 'high' ? 'active' : ''}`} onClick={() => applyQuickFilter('high')}>High Value ({'>'}₹5k)</button>
        </div>
      </div>

      <div className="results-header">
        <div className="results-count">Showing <strong>{processedData.length}</strong> transactions</div>
        <div className="results-total">Total: <span>₹{totalFiltered.toLocaleString('en-IN')}</span></div>
      </div>

      <div className="transactions-table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              {isSelectionMode && <th style={{ width: 40, textAlign: 'center' }}><input type="checkbox" className="custom-checkbox" onChange={handleSelectAll} checked={processedData.length > 0 && selectedIds.length === processedData.length} /></th>}
              <th className="sortable-th" onClick={() => handleSort('date')}><div className="th-content">Date <SortIndicator column="date" /></div></th>
              <th className="sortable-th" onClick={() => handleSort('source')}><div className="th-content">Source <SortIndicator column="source" /></div></th>
              <th>Description</th>
              <th className="sortable-th" onClick={() => handleSort('amount')}><div className="th-content">Amount <SortIndicator column="amount" /></div></th>
              <th style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {processedData.length > 0 ? (
              processedData.map((entry, idx) => (
                <tr
                  key={idx}
                  className={`table-row ${selectedIds.includes(entry.id) ? 'selected-row' : ''}`}
                  onClick={(e) => handleRowClick(entry.id, e)}
                  style={{ cursor: isSelectionMode ? 'pointer' : 'default' }}
                >
                  {isSelectionMode && <td style={{ textAlign: 'center' }}><input type="checkbox" className="custom-checkbox" checked={selectedIds.includes(entry.id)} readOnly /></td>}
                  <td>{entry.date}</td>
                  <td><span className="source-badge">{entry.source}</span></td>
                  <td className="description-cell">{entry.description || <span className="text-muted">—</span>}</td>
                  <td className="amount-cell">₹{entry.amount.toLocaleString('en-IN')}</td>
                  <td className="action-cell">
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn-icon-micro" onClick={(e) => { e.stopPropagation(); setEditingItem(entry) }} title="Edit"><Edit2 size={14} /></button>
                      <button className="btn-icon-micro danger" onClick={(e) => handleSingleDelete(entry.id, e)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="empty-filter-state"><Search size={48} /><p>No results.</p></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showExportMenu && (
        <DropdownPortal rect={exportButtonRect} onClose={() => setShowExportMenu(false)}>
          <button onClick={handleExcelExport}><span className="file-icon excel">X</span> Excel Report (.xlsx)</button>
          <button onClick={handlePDFExport}><span className="file-icon pdf">P</span> PDF Invoice (.pdf)</button>
        </DropdownPortal>
      )}

      <AnimatePresence>
        {selectedIds.length > 0 && (
          <motion.div className="floating-action-bar" initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}>
            <span>{selectedIds.length} items selected</span>
            <button className="btn-danger-sm" onClick={handleBulkDelete}><Trash2 size={16} /> Delete Selected</button>
          </motion.div>
        )}
      </AnimatePresence>

      {editingItem && (
        <div className="modal-overlay">
          <div className="modal-card-modern">
            <div className="modal-header-modern"><h3>Edit Transaction</h3><button className="btn-close-modern" onClick={() => setEditingItem(null)}><X size={18} /></button></div>
            <form onSubmit={handleUpdate}>
              <div className="form-grid-modern">
                <div className="form-group"><label>Date</label><input type="date" className="input-modern" value={editingItem.date} onChange={e => setEditingItem({ ...editingItem, date: e.target.value })} required /></div>
                <div className="form-group"><label>Amount</label><input type="number" className="input-modern" value={editingItem.amount} onChange={e => setEditingItem({ ...editingItem, amount: e.target.value })} required /></div>
                <div className="form-group full"><label>Source</label><select className="input-modern" value={editingItem.source} onChange={e => setEditingItem({ ...editingItem, source: e.target.value })}>{allSources.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group full"><label>Description</label><input type="text" className="input-modern" value={editingItem.description} onChange={e => setEditingItem({ ...editingItem, description: e.target.value })} /></div>
              </div>
              <div className="modal-footer-modern">
                <button type="button" className="btn-modern-sec" onClick={() => setEditingItem(null)}>Cancel</button>
                <button type="submit" className="btn-modern-pri">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
}
